import { VisitorData, VisitorVariations, VisitorVariationUpdateParam } from '@src/container/handleIframeMessage/types';
import { Dispatch } from 'react';

export enum FsTargetingOperator {
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
  CONTAINS = 'CONTAINS',
  NOT_CONTAINS = 'NOT_CONTAINS',
  EXISTS = 'EXISTS',
  NOT_EXISTS = 'NOT_EXISTS',
  GREATER_THAN = 'GREATER_THAN',
  LOWER_THAN = 'LOWER_THAN',
  GREATER_THAN_OR_EQUALS = 'GREATER_THAN_OR_EQUALS',
  LOWER_THAN_OR_EQUALS = 'LOWER_THAN_OR_EQUALS',
  STARTS_WITH = 'STARTS_WITH',
  ENDS_WITH = 'ENDS_WITH',
}

export enum UserRole {
  ACCOUNT_OWNER = 'ACCOUNT_OWNER',
  GUEST = 'GUEST',
  MEMBER = 'MEMBER',
  PROJECT_MANAGER = 'PROJECT_MANAGER',
  SUPER_ADMIN = 'SUPER_ADMIN',
}

export type Targetings = {
  operator: FsTargetingOperator;
  key: string | 'fs_all_users' | 'fs_users';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  hasMatched?: boolean;
};

export type FsVariation = {
  id: string;
  name?: string;
  modifications: {
    type: string;
    value: unknown;
  };
  allocation?: number;
  reference?: boolean;
  isOrigin?: boolean;
  variationGroupId?: string;
  variationGroupName?: string;
};

export enum CampaignType {
  ab = 'ab',
  toggle = 'toggle',
  perso = 'perso',
  deployment = 'deployment',
}

export type FsVariationToForce = {
  campaignId: string;
  campaignName: string;
  campaignType: CampaignType;
  CampaignSlug?: string | null;
  variationGroupId: string;
  variationGroupName?: string;
  variation: FsVariation;
};

export type TargetingGroups = {
  targetings: Targetings[];
};

export type VariationGroupDTO = {
  id: string;
  name?: string;
  targeting: {
    targetingGroups: TargetingGroups[];
  };
  variations: FsVariation[];
};

export enum CampaignDisplayStatus {
  SHOWN = 'SHOWN',
  HIDDEN = 'HIDDEN',
  RESET = 'RESET',
}

export type FsCampaign = {
  id: string;
  name: string;
  type: CampaignType;
  status: WebSDKCampaignStatus;
  hasTargetingMatched?: boolean;
  displayStatus: CampaignDisplayStatus;
  slug?: string | null;
  variationGroups: VariationGroupDTO[];
};

export type UserAccess = {
  items: {
    id: string;
    role: UserRole;
    account_environment: {
      id: string;
      is_main: boolean;
      environment: string;
      account: {
        id: string;
        name: string;
      };
    };
  }[];
  current_items_count: number;
  current_page: number;
  total_count: number;
  items_per_page: number;
  last_page: number;
};

export type BucketingDTO = {
  panic?: boolean;
  campaigns?: FsCampaign[];
  accountSettings?: {
    enabledXPC?: true;
    troubleshooting?: {
      startDate: string;
      endDate: string;
      traffic: number;
      timezone: string;
    };
  };
};

export type LoadingStatus = 'fulfilled' | 'rejected' | 'pending';

export type User = {
  phone: null;
  validated: true;
  username: string;
  prefersColorScheme: string;
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  linkedin: string | null;
  roles: string[];
  company: null;
  department: null;
  jobTitle: null;
};

export type UserData = { user: User; access?: UserAccess };

export type FsAuthContextValue = {
  userData?: UserData;
  addUserData: (arg: UserData) => void;
};

export type UserRestrictedProps = {
  toggleRetryAuth: () => void;
  retryAuth?: boolean;
};

export type FsDataState = {
  /** Environment ID for the current Flagship instance */
  fsEnvId: string;

  /** Bucketing configuration containing campaigns and account settings */
  bucketingFile?: BucketingDTO;

  /**
   * Visitor allocation data - maps campaign IDs to their allocated variations
   * Represents variations assigned through bucketing process
   */
  allocatedVariations?: Record<string, VisitorVariations>;

  /**
   * Visitor exposure data - maps campaign IDs to variations the visitor actually saw
   * Represents variations that were actually displayed/rendered
   */
  exposedVariations?: Record<string, VisitorVariations>;

  /**
   * All campaigns where the visitor is allocated to a variation (ACCEPTED status)
   * Truth source - Original unfiltered campaign data from bucketing
   */
  allAcceptedCampaigns: FsCampaign[];

  /**
   * All campaigns where the visitor is not allocated to any variation (REJECTED status)
   * Truth source - Original unfiltered campaign data from bucketing
   */
  allRejectedCampaigns: FsCampaign[];

  /**
   * All campaigns where the visitor was exposed to a variation
   * Truth source - Original unfiltered campaign data from actual exposure
   */
  allExposedCampaigns: FsCampaign[];

  /**
   * Manual overrides: Campaigns where the visitor is forcefully allocated to a specific variation
   * Maps campaign IDs to forced variation configurations
   */
  variationsForcedAllocation: Record<string, FsVariationToForce>;

  /**
   * Manual overrides: Campaigns where the visitor is forcefully excluded from variations
   * Maps campaign IDs to forced exclusion configurations
   */
  variationsForcedUnallocation: Record<string, FsVariationToForce>;

  /**
   * UI-filtered view: Campaigns shown in the exposed section
   * Subset of allExposedCampaigns after applying display filters
   */
  displayedExposedCampaigns: FsCampaign[];

  /**
   * UI-filtered view: Campaigns shown in the allocated but not exposed section
   * Subset of allAcceptedCampaigns after applying display filters
   */
  displayedUnexposedCampaigns: FsCampaign[];

  /**
   * UI-filtered view: Campaigns shown in the rejected section
   * Subset of allRejectedCampaigns after applying display filters
   */
  displayedRejectedCampaigns: FsCampaign[];

  /**
   * Control flag: Whether to send forced unallocation messages to the iframe
   * Used to synchronize manual exclusions with the embedded SDK
   */
  shouldSendForcedUnallocation: boolean;

  /**
   * Control flag: Whether to send forced allocation messages to the iframe
   * Used to synchronize manual allocations with the embedded SDK
   */
  shouldSendForcedAllocation: boolean;

  /** Current visitor context data including ID and custom attributes */
  visitorData?: VisitorData;

  /**
   * Flag to track if exposure event has been received for the current session
   */
  hasReceivedExposureEvent?: boolean;

  /** SDK version used by the embedded Flagship instance */
  sdkInfo?: SdkInfo;
};

export type SdkInfo = {
  name: string;
  version: string;
  tag: string;
};

export type FsReducerActionUpdateVisitorVariation = {
  type: 'updateVisitorVariations' | 'updateVisitorExposedVariations';
  value: Record<string, VisitorVariations>;
  param?: VisitorVariationUpdateParam;
  visitorData?: VisitorData;
  sdkInfo?: SdkInfo;
};

export type FsVariationAllocationAction = {
  type:
    | 'forceVariationAllocation'
    | 'forceVariationUnallocation'
    | 'removeForcedVariationAllocation'
    | 'removeForcedVariationUnallocation';
  value: FsVariationToForce;
};

export type ShouldSendForcedAllocationAction = {
  type: 'setShouldSendForcedAllocation';
  value: boolean;
};

export type ShouldSendForcedUnallocationAction = {
  type: 'setShouldSendForcedUnallocation';
  value: boolean;
};

export type FsSearchCampaigns = { type: 'searchFsCampaigns'; value: string };
export type FsReducerAction =
  | { type: 'setBucketingFile'; bucketingFile: BucketingDTO }
  | { type: 'renderCampaignsAfterDelay' }
  | FsSearchCampaigns
  | FsReducerActionUpdateVisitorVariation
  | FsVariationAllocationAction
  | ShouldSendForcedAllocationAction
  | ShouldSendForcedUnallocationAction;

export type FsContextValue = {
  dispatch: Dispatch<FsReducerAction>;
  fsDataState: FsDataState;
};

export type FsVariationToForceState = {
  variationsToForce: Record<string, FsVariationToForce>;
  variationsToForceTemp: Record<string, FsVariationToForce>;
};

export type FsVariationToForceReducerAction =
  | {
      type: 'addVariationToForceTemp';
      value: Record<string, FsVariationToForce>;
    }
  | {
      type: 'addVariationToForce';
      value: Record<string, FsVariationToForce>;
    }
  | {
      type: 'clearVariationsToForce';
    }
  | {
      type: 'removeVariationToForce' | 'removeVariationToForceTemp' | 'removeAllVariationToForce';
      value: string[];
    };

export type FsVariationToForceContextValue = {
  fsVariationToForceState: FsVariationToForceState;
  dispatchFsVariationToForce: Dispatch<FsVariationToForceReducerAction>;
};

export type FsCampaignData = {
  variations: FsVariation[];
  targetingGroups: {
    targetings: Targetings[];
  }[];
};

export type FsEventState = {
  hits: Record<string, unknown>[];
};

export type FsEventReducerAction =
  | {
      type: 'add';
      value: Record<string, unknown>[];
    }
  | { type: 'clearHits' };

export type FsEventContextValue = {
  fsHitsState: FsEventState;
  fsHitsDispatch: Dispatch<FsEventReducerAction>;
};
export type ShowToast = {
  text?: string;
  type?: 'SUCCESS' | 'WARNING' | 'DANGER' | 'INFO';
  dataTestId?: string;
  className?: string;
};

export type ToastInitialValue = {
  showToast: (arg: ShowToast) => void;
};

export type WrapperPromise<T> = {
  read(): T | undefined;
};

/**
 * CampaignStatus represents the relationship between a visitor and a campaign.
 *
 */
export enum WebSDKCampaignStatus {
  /** Visitor is allocated to a variation through normal bucketing process */
  ACCEPTED = 'ACCEPTED',
  /** Visitor is not allocated to any variation in this campaign */
  REJECTED = 'REJECTED',
}

export enum FsQAFeatureFlag {
  ENABLE_FORCE_ALLOCATION = 'ENABLE_FORCE_ALLOCATION',
}
